import React, { useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const ContactSectionNew = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    clinic: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Thank you for your interest!",
      description: "We will contact you as soon as possible.",
    });
    
    setFormData({
      name: '',
      clinic: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 px-6 bg-gradient-to-b from-white to-gray-50">
      <Toaster />
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-5xl md:text-6xl font-light mb-4 text-gray-900">Contact us</h2>
          <p className="text-gray-600 text-lg font-light max-w-2xl mx-auto">
            Thank you for your interest in MCT. Fill out the form below and we will contact you as soon as possible.
          </p>
        </div>

        {/* Split Layout: Left Text | Right Form */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: Persuasive Text */}
          <div className="space-y-8 animate-fade-in">
            <div>
              <h3 className="text-3xl md:text-4xl font-light text-gray-900 mb-6" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Elevate Your Practice to
                <br />
                <span className="text-teal-500 font-normal">World-Class Standards</span>
              </h3>
              <p className="text-gray-600 font-light leading-relaxed mb-8 text-lg">
                Join the global network of pioneering clinics who have transformed their regenerative medicine offerings with MCT's cutting-edge photothermal biomodulation technology.
              </p>
              <p className="text-gray-600 font-light leading-relaxed mb-8">
                Our dedicated team of experts will guide you through every step—from initial consultation to full implementation and ongoing support.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Expert Consultation</h4>
                  <p className="text-gray-600 font-light text-sm">
                    Our team will guide you through integration and best practices
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Rapid ROI</h4>
                  <p className="text-gray-600 font-light text-sm">
                    See measurable results and increased patient satisfaction quickly
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Ongoing Support</h4>
                  <p className="text-gray-600 font-light text-sm">
                    Comprehensive training and continuous technical assistance
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 font-light">
                <strong className="text-gray-700">Response time:</strong> Within 24 hours for all inquiries
              </p>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-gray-100 animate-fade-in-scale">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-light mb-2 text-sm">
                  Name <span className="text-teal-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent font-light transition-all"
                  placeholder="Dr. John Smith"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-light mb-2 text-sm">
                  Clinic <span className="text-teal-500">*</span>
                </label>
                <input
                  type="text"
                  name="clinic"
                  value={formData.clinic}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent font-light transition-all"
                  placeholder="Medical Aesthetics Center"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-light mb-2 text-sm">
                  Email <span className="text-teal-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent font-light transition-all"
                  placeholder="doctor@clinic.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-light mb-2 text-sm">
                  Phone <span className="text-teal-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent font-light transition-all"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-light mb-2 text-sm">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent font-light resize-none transition-all"
                  placeholder="Tell us about your clinic and how we can help..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-500 hover:to-teal-600 text-white font-medium py-5 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] text-lg"
              >
                Request Your Consultation
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSectionNew;
